import { CheckCircleOutlined, CloseCircleOutlined, EnvironmentOutlined, HomeOutlined, TeamOutlined, UserDeleteOutlined, UserOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { Card, Col, Row, Statistic, Progress, Typography } from "antd";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import { getEmployees } from "../../../core/data/redux/employee/employeeAction";
import { fetchAllDepartments } from "../../../core/data/redux/department/departmentAction";
import { filterByStatus } from "../../../core/utils/filterActiveData";
import { fetchHRwiseWorkforceEmployees } from "../../../core/data/redux/hr-dashboard/hrDashboardAction";
import Table from "../../../core/common/dataTable/index";
const { Text } = Typography;

interface WorkforceOverviewProps {
  data?: any;
  selectedCategory: string | null;
  setSelectedCategory: (value: string | null) => void;
  selectedTableData: any[];
  setSelectedTableData: (data: any[]) => void;
  handleCardClick: (key: string) => void;
  tableKey: number;
}

const WorkforceOverview: React.FC<WorkforceOverviewProps> = ({
  data,
  selectedCategory,
  setSelectedCategory,
  selectedTableData,
  setSelectedTableData,
  handleCardClick,
  tableKey
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { departments } = useSelector((state: any) => state.department);
  const activeDepartment = filterByStatus(departments, 'active', 'Status')
  const { employees } = useSelector((state: RootState) => state.employee);
  const monthwiseData = useSelector((state: any) => state.dashboard.hrDashboard.monthwiseData);
  const hrEmployees = useSelector((state: any) => state.dashboard.hrDashboard.hrEmployees);

  //Hires and Exits
  const hiresExitsData = Array.isArray(monthwiseData?.data?.newHiresExitsMonthly)
    ? monthwiseData.data.newHiresExitsMonthly.map((item: any) => ({
      month: item.month,
      hires: Number(item.new_hires),
      exits: Number(item.exits),
    }))
    : [];

  //Department chart
  const distributionData = activeDepartment.map((dept) => ({
    name: dept.Name,
    value: dept.totalEmployees
  }));

  const COLORS = ["#1890ff", "#52c41a", "#faad14", "#eb2f96"];

  useEffect(() => {
    if (!departments.length) {
      dispatch(fetchAllDepartments());
    }
  }, [departments.length, dispatch]);

  useEffect(() => { if (!employees.length) { dispatch(getEmployees()) } })

  return (
    <>
      <Row gutter={[24, 24]} className="mb-4">
        <Col xs={24} sm={12} lg={6}>
          <Card className="h-100">
            <Statistic
              title="Total Employees"
              value={monthwiseData?.data?.hrWiseEmployees?.[0]?.total_employees || 0}
              prefix={<TeamOutlined className="text-primary" />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="h-100" onClick={() => handleCardClick("active")} style={{ cursor: "pointer" }}>
            <Statistic
              title="Active Employees"
              value={monthwiseData?.data?.hrWiseEmployees?.[0]?.active_employees || 0}
              prefix={<UserOutlined className="text-success" />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="h-100" onClick={() => handleCardClick("leave")} style={{ cursor: "pointer" }}>
            <Statistic
              title="On Leave"
              value={monthwiseData?.data?.hrWiseEmployees?.[0]?.on_leave_today || 0}
              prefix={<UserSwitchOutlined className="text-warning" />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="h-100" onClick={() => handleCardClick("wfh")} style={{ cursor: "pointer" }}>
            <Statistic
              title="Work From Home"
              value={monthwiseData?.data?.hrWiseEmployees?.[0]?.wfh_today || 0}
              prefix={<HomeOutlined style={{ color: "brown" }} />}
              valueStyle={{ color: "lightcoral" }}
            />
          </Card>
        </Col>
      </Row>
      <Row gutter={[24, 24]} className="mb-4">
        <Col xs={24} sm={12} lg={6}>
          <Card className="h-100" onClick={() => handleCardClick("present")} style={{ cursor: "pointer" }}>
            <Statistic
              title="Present"
              value={monthwiseData?.data?.hrWiseEmployees?.[0]?.present_today || 0}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "green" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="h-100" onClick={() => handleCardClick("absent")} style={{ cursor: "pointer" }}>
            <Statistic
              title="Absent"
              value={monthwiseData?.data?.hrWiseEmployees?.[0]?.absent_today || 0}
              prefix={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
              valueStyle={{ color: "red" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="h-100" onClick={() => handleCardClick("onsite")} style={{ cursor: "pointer" }}>
            <Statistic
              title="On Site"
              value={monthwiseData?.data?.hrWiseEmployees?.[0]?.onsite_employees || 0}
              prefix={<EnvironmentOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "blue" }}
            />
          </Card>
        </Col>
      </Row>
      {selectedCategory && (
  <div className="card mt-4">
    <div className="card-header">
      <h5 className="mb-0 text-capitalize">
        {selectedCategory === "attrition" && "Department-wise Attrition Rate"}
        {selectedCategory === "retention" && "Department-wise Retention Rate"}
        {selectedCategory !== "attrition" &&
          selectedCategory !== "retention" &&
          `${selectedCategory} Employees`}
      </h5>
    </div>

    <div className="card-body p-0">
      <Table
        key={tableKey}
        dataSource={selectedTableData}
        columns={
          selectedCategory === "attrition"
            ? [
                { title: "Department", dataIndex: "departmentName" },
                {
                  title: "Total Employees",
                  dataIndex: "total_employees",
                },
                {
                  title: "Inactive Employees",
                  dataIndex: "inactive_employees",
                },
                {
                  title: "Attrition Rate",
                  dataIndex: "attrition_rate",
                },
              ]
            : selectedCategory === "retention"
            ? [
                { title: "Department", dataIndex: "departmentName" },
                {
                  title: "Total Employees",
                  dataIndex: "total_employees",
                },
                {
                  title: "Active Employees",
                  dataIndex: "active_employees",
                },
                {
                  title: "Retention Rate",
                  dataIndex: "retention_rate",
                },
              ]
            : [
                { title: "Employee ID", dataIndex: "EmployeeID" },
                { title: "Name", dataIndex: "employeeName" },
                { title: "Department", dataIndex: "departmentName" },
                { title: "Designation", dataIndex: "designationName" },
                { title: "Supervisor", dataIndex: "supervisorName" },
                { title: "HOD", dataIndex: "hodName" },
                { title: "Email", dataIndex: "Email" },
                { title: "Phone", dataIndex: "Phone" },
              ]
        }
      />
    </div>
  </div>
)}

      <Row gutter={[24, 24]} className="mb-4">
        <Col xs={24} lg={12}>
          <Card title="New Hires vs Exits (Monthly)">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={hiresExitsData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="hires" fill="#52c41a" name="New Hires" />
                <Bar dataKey="exits" fill="#ff4d4f" name="Exits" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Employee Distribution (by Department)">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={distributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={false}
                >
                  {distributionData.map((entry, index: any) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
      <Row gutter={[24, 24]} className="mb-4">
        <Col xs={24} sm={12} lg={12}>
          <Card className="h-100" style={{ cursor: "pointer" }} onClick={() => handleCardClick("attrition")} >
            <Text className="d-block mb-2">Attrition Rate</Text>
            <Progress
              percent={parseFloat(monthwiseData?.data?.hrWiseEmployees?.[0]?.attrition_rate) || 0}
              status="active"
              strokeColor="#ff4d4f"
              format={(percent) => `${percent ?? 0}%`}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={12}>
          <Card className="h-100" style={{ cursor: "pointer" }} onClick={() => handleCardClick("retention")}>
            <Text className="d-block mb-2">Retention Rate</Text>
            <Progress
              percent={parseFloat(monthwiseData?.data?.hrWiseEmployees?.[0]?.retention_rate) || 0}
              status="active"
              strokeColor="#52c41a"
              format={(percent) => `${percent ?? 0}%`}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default WorkforceOverview;
